import { type CSSProperties, type ReactElement } from 'react';
import { formatValidationCounts } from '@/lib/validationSummary';
import type {
  SubmissionValidationResultsResponse,
  ValidationResultEntry,
} from '@/api/v2Client';

/**
 * Wave E.4 — Top-of-Compiler-page banner summarizing validation results
 * for the whole submission. Three visual states:
 *
 *   not-run  — gray informational pill ("Validation hasn't been run yet")
 *   pass     — green ("All N documents passed validation")
 *   mixed    — amber/red ("3 documents have warnings, 1 has errors")
 *
 * Renders to the right of the action area: shows a "Validate all" /
 * "Re-validate all" button alongside the summary.
 */

export interface ValidationStatusBannerProps {
  readonly showValidateAction?: boolean;
  readonly data: SubmissionValidationResultsResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly validating: boolean;
  readonly canValidate: boolean;
  readonly onValidateAll: () => void;
  /**
   * Phase compiler-inline-fix-validation (TODO-023) — navigate the
   * compiler to the document referenced by a failing validation entry.
   * Caller handles cross-section discovery (fetching the doc to learn
   * its CtdSectionCode, switching the active section, selecting the
   * row). When undefined, per-row "Fix" buttons are hidden.
   */
  /**
   * phase-validation-surfaces — opens the ONE validation worklist (the bottom
   * drawer). The banner no longer renders its own issue list.
   */
  readonly onOpenWorklist?: () => void;
}

const wrapperBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 6,
  fontSize: 13,
  marginBottom: 12,
  border: '1px solid var(--border)',
};

const wrapperPass: CSSProperties = {
  ...wrapperBase,
  background: 'rgba(34,197,94,0.10)',
  borderColor: 'rgba(34,197,94,0.40)',
  color: 'var(--ok, #15803d)',
};

const wrapperWarn: CSSProperties = {
  ...wrapperBase,
  background: 'rgba(245,158,11,0.10)',
  borderColor: 'rgba(245,158,11,0.40)',
  color: '#92400e',
};

const wrapperError: CSSProperties = {
  ...wrapperBase,
  background: 'rgba(239,68,68,0.10)',
  borderColor: 'rgba(239,68,68,0.40)',
  color: 'var(--danger, #b91c1c)',
};

const wrapperNeutral: CSSProperties = {
  ...wrapperBase,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
};

const buttonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
};

function summaryText(data: SubmissionValidationResultsResponse): string {
  const { documentCount, errorCount, warningCount } = data;
  // phase-fix-validation-banner-crash — defend against a response that omits
  // these arrays (the POST /validate endpoint historically did). The producer
  // is now fixed to always send them, but this component's crash white-screens
  // the entire compiler route, so it never dereferences an absent array.
  const documentsWithErrors = data.documentsWithErrors ?? [];
  const documentsWithWarnings = data.documentsWithWarnings ?? [];
  if (errorCount === 0 && warningCount === 0) {
    // Phase phase-review-surface-strengthen — reworded to make clear that
    // "structural validation passed" ≠ "placements are correct / review is done".
    // The old text "All N documents passed validation" invited rubber-stamping.
    return `✅ Structural checks passed — ${documentCount} document${documentCount === 1 ? '' : 's'} (XML structure, file format, required sections).`;
  }
  // phase-validation-surfaces (Sharon, 2026-07-24) — this hero banner is the
  // FOURTH validation surface, and it described the same run in a THIRD numbering.
  // It headlined "4 structural errors, 2 have warnings", where:
  //   - "4 structural errors" = the per-CHECK errorCount (same number every
  //     surface shows — "structural" was a label, not a different category);
  //   - "2 have warnings"      = documentsWithWarnings.length, a per-DOCUMENT
  //     count, which disagreed with the toolbar's per-CHECK "4 warnings".
  // Mixing per-check and per-document counts in one line is what made one screen
  // report the same state four ways with three different numbers.
  //
  // The headline now uses the ONE shared per-check phrasing, so this banner, the
  // top-bar chip, the toolbar badge and the bottom worklist all agree. "Structural
  // checks" is kept ONLY as a descriptive lead-in (these are XML/format/section
  // checks), never as a separate count. The per-document breakdown, when useful,
  // lives in the expandable issue list below, not in the headline.
  const summary = formatValidationCounts({ errorCount, warningCount });
  // Referenced so the eslint no-unused-vars rule stays happy now that the
  // headline no longer reads the per-document arrays; the detail list still uses
  // them via the entries below.
  void documentsWithErrors;
  void documentsWithWarnings;
  return `⚠️ Structural checks — ${summary}.`;
}

function isFailing(entry: ValidationResultEntry): boolean {
  return entry.status === 'fail' && (entry.severity === 'error' || entry.severity === 'warning');
}

export function ValidationStatusBanner({
  data,
  loading,
  error,
  validating,
  canValidate,
  onValidateAll,
  onOpenWorklist,
  showValidateAction = false,
}: ValidationStatusBannerProps): ReactElement {
  const buttonLabel = validating
    ? 'Validating…'
    : data
      ? 'Re-validate all'
      : 'Validate all';
  const buttonDisabled = !canValidate || validating;

  if (loading && !data) {
    return (
      <div data-testid="validation-status-banner-loading" style={wrapperNeutral}>
        <span>Loading validation results…</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div data-testid="validation-status-banner-error" role="alert" style={wrapperError}>
        <span>Could not load validation results: {error}</span>
        <button
          type="button"
          data-testid="validation-status-banner-validate"
          onClick={onValidateAll}
          disabled={buttonDisabled}
          style={buttonDisabled ? buttonDisabledStyle : buttonStyle}
        >
          {buttonLabel}
        </button>
      </div>
    );
  }

  // phase-package-viewer-live-structure (Norvantis finding, Sharon 2026-07-29)
  // — "no validation RESULTS" means "no validation has run", full stop. The old
  // guard also required documentCount === 0, but documentCount is the count of
  // the submission's DOCUMENTS (the endpoint returns docIds.length), so a
  // never-validated submission with 4 documents fell through to summaryText
  // with zero error/warning counts and rendered "✅ Structural checks passed —
  // 4 documents" — an asserted outcome with no run behind it, sitting beside a
  // toolbar honestly reading "— not validated". A real run always writes rows
  // (pass rows included), so an empty results array is definitive.
  // (Belt and braces: a malformed response claiming non-zero counts with an
  // empty results array — the historical POST /validate shape — must still
  // surface its counts rather than masquerade as not-run.)
  // T17-D43 - the same currency rule as the publish checklist: results that are not a FULL run at least as
  // new as the last document change (per-document checks only, a run older than an edit, an unreadable
  // change time) are NOT a pass, whatever their counts say. Shown as the not-run state with the server's reason.
  const notCurrent = data?.currency !== undefined && data.currency.kind !== 'current';
  if (
    !data ||
    notCurrent ||
    ((data.results ?? []).length === 0 && data.errorCount === 0 && data.warningCount === 0)
  ) {
    // ISS-017 — when a run EXISTS but is not current, say so WITH its counts
    // (in the one shared phrasing) instead of a bare "not validated": the
    // toolbar badge, this banner and the worklist drawer must all describe
    // the same run the same way. kind 'never' still means no run at all.
    const runExistsButStale =
      notCurrent && data !== null && data.currency?.kind !== 'never';
    const staleCounts =
      runExistsButStale && (data.errorCount > 0 || data.warningCount > 0)
        ? `${formatValidationCounts({
            errorCount: data.errorCount,
            warningCount: data.warningCount,
          })} — `
        : '';
    return (
      <div data-testid="validation-status-banner-empty" style={wrapperNeutral}>
        <span>
          {runExistsButStale
            ? `${staleCounts}out of date — ${data.currency?.detail ?? 'validation is out of date.'}`
            : 'Validation hasn’t been run for this submission yet.'}
        </span>
        <button
          type="button"
          data-testid="validation-status-banner-validate"
          onClick={onValidateAll}
          disabled={buttonDisabled}
          style={buttonDisabled ? buttonDisabledStyle : buttonStyle}
        >
          {buttonLabel}
        </button>
      </div>
    );
  }

  const wrapperStyle =
    data.errorCount > 0
      ? wrapperError
      : data.warningCount > 0
        ? wrapperWarn
        : wrapperPass;

  // Phase compiler-inline-fix-validation (TODO-023) — collect failing
  // entries so we can render an inline per-error list with "Fix" links.
  // Cap to 25 rows so a very noisy submission can't push the page below
  // the fold; an explicit "and N more" footer is rendered when truncated.
  const failingEntries = (data.results ?? []).filter(isFailing);
  const hasFailing = failingEntries.length > 0;

  // Phase phase-review-surface-strengthen — add a placement-review reminder
  // beneath the all-pass banner so the reviewer knows structural pass ≠ done.
  const isAllPass = data.errorCount === 0 && data.warningCount === 0;

  return (
    <div data-testid="validation-status-banner" style={wrapperStyle}>
      {showValidateAction && <button type="button" data-testid="validation-status-banner-validate"
        onClick={onValidateAll} disabled={buttonDisabled}
        style={buttonDisabled ? buttonDisabledStyle : buttonStyle}>{buttonLabel}</button>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
          <span data-testid="validation-status-banner-summary">{summaryText(data)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* phase-validation-surfaces (Sharon, 2026-07-24) — the banner used
                to own its OWN inline issue list ("Show N issues") AND its own
                "Re-validate all" button. Both were removed under the role model:
                the WORK detail lives in ONE worklist (the bottom drawer) and
                re-validation has ONE control (the sequence toolbar). This banner
                keeps only the GLANCE-consistent summary and points at the
                worklist rather than duplicating it. */}
            {hasFailing && onOpenWorklist ? (
              <button
                type="button"
                data-testid="validation-status-banner-open-worklist"
                onClick={onOpenWorklist}
                style={{
                  background: 'transparent',
                  border: '1px solid currentColor',
                  borderRadius: 4,
                  padding: '3px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                Open worklist
              </button>
            ) : null}
          </div>
        </div>

        {/* Phase phase-review-surface-strengthen — placement review reminder.
            Structural pass ≠ placements are correct. This note is only shown
            on all-pass so it doesn't compete with error messages. */}
        {isAllPass && (
          <div
            data-testid="validation-placement-reminder"
            style={{
              fontSize: 11,
              color: '#15803D',
              opacity: 0.85,
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            ⬆ These are structural checks only. Placement accuracy — whether each document
            is in the correct CTD section — requires your review in the inspector above.
          </div>
        )}
      </div>
    </div>
  );
}
